import React, { useContext, useEffect, useState } from 'react'
import { bool, func } from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'
import clsx from 'clsx'

// Import Components
import { Search as SearchIcon } from '../svgs/Search'
import { SiteConfigContext } from '../SiteConfigProvider'
import { Button, Link } from '@jelly/ui'
import FeaturedTopics from '../HeaderBanner/FeaturedTopics'
import { MegaPhone } from '../svgs/MegaPhone'
import { CommunityIcon } from '../svgs/CommunityIcon'

import { withStyles } from '@material-ui/core/styles'
import Hidden from '@material-ui/core/Hidden'
import Paper from '@material-ui/core/Paper'
import Slide from '@material-ui/core/Slide'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordionDetails from '@material-ui/core/AccordionDetails'
import { makeStyles } from '@material-ui/core/styles'
import { HAMBURGER_MENU_WIDTH } from '.'
import { Arrow } from '../svgs/Arrow'
import { Chevron } from '../svgs/Chevron'
import { trackNavEvent } from '../HeaderBanner'
import { EmailSubscribeContext } from '../EmailSubscribeProvider'

// Styled Components via Emotion

const useStyles = ({ hamburgerMenuWidth, isHamburgerMenuOpen }) =>
	makeStyles(
		(theme) => {
			return {
				BusinessTopicsTitle: {
					color: theme.palette.tertiary.main,
					fontFamily: theme.typography.subtitle2.fontFamily,
					marginBottom: '10px',
					listStyleType: 'none',
					paddingTop: '2rem',
					textTransform: 'uppercase',
				},
				HubTitle: {
					fontFamily: theme.typography.h2.fontFamily,
					display: 'flex',
					fontSize: '1.28rem',
					justifyContent: 'space-between',
				},
				Menu: {
					background: theme.palette.common.white,
					display: 'inline-block',
					height: '100%',
					paddingLeft: '3rem',
					paddingTop: '80px',
					width: '100%',
					[theme.breakpoints.up('sm', 'md')]: {
						maxWidth: hamburgerMenuWidth,
					},
					[theme.breakpoints.up('md')]: {
						paddingLeft: '5rem',
						width: hamburgerMenuWidth,
					},
				},
				MenuOverlay: {
					height: '100vh',
					position: 'absolute',
					top: '0',
					width: '100%',
					left: isHamburgerMenuOpen ? '0px' : '-100%',
					transition: 'all 0.8s ease',

					[theme.breakpoints.up('sm')]: {
						maxWidth: ({ hamburgerMenuWidth }) => hamburgerMenuWidth,
						position: 'relative',
					},
				},
				PanelLink: {
					fontSize: '1rem',
				},
				SeeAllButton: {
					height: '0',
					left: '0',
					marginLeft: '1rem',
					marginRight: 'auto',
					padding: '.75rem .5rem',
					width: 'auto',
				},
				StyledLink: {
					alignItems: 'center',
					color: theme.palette.primary.main,
					display: 'flex',
					fontFamily: theme.typography.h2.fontFamily,
					flexDirection: 'row',
					cursor: 'pointer',
					lineHeight: '25px',
					width: 'fit-content',
					'&.search': {
						'& svg': {
							marginLeft: '.5rem',
						},
					},
					'&.featuredTopic': {
						marginTop: '.5rem',
					},
					'&:hover': {
						'&.svgOverride': {
							'& *': {
								stroke: theme.palette.secondary.main,
							},
						},
					},
				},
				SubHubHamburgerMenuWrapper: {
					position: 'inherit',
					top: '0',
					left: isHamburgerMenuOpen ? '0px' : '-100%',
					visibility: isHamburgerMenuOpen ? 'visible' : 'hidden',
					width: '100%',

					[theme.breakpoints.up('sm')]: {
						position: 'absolute',
					},
				},
				ToolBar: {
					display: 'none',
					margin: '10px 0',
					paddingLeft: '65px',

					[theme.breakpoints.up('sm')]: {
						display: 'flex',
						flexDirection: 'row',
						paddingLeft: '85px',
						marginBottom: '30px',
					},
				},
			}
		},
		{ name: 'subHubHamburgerMenu' }
	)

const Separator = styled.span`
	width: 25px;
	line-height: 25px;
	text-align: center;
`
const HubsContainer = styled.div`
	height: calc(100vh - 60%);
	overflow-y: auto;
`
const HubTitleWrapper = styled.div`
	align-items: center;
	display: flex;
	justify-content: space-between;
	margin-right: 1rem;
	width: 100%;
`
const FeaturedArrow = styled.div`
	transform: rotate(270deg);
	margin-left: 0.75rem;
`

// MENU STYLES //
const MenuContainer = styled.div`
	width: 100%;
	height: 100%;
	position: relative;
	z-index: 4;

	${({ theme }) => theme.breakpoints.up('sm')} {
		max-width: 430px;
		z-index: 5; /* so the account panel always slides in from under the main menu on breakpoints larger than mobile */
	}
`
// DEFAULT TOOL BAR (LINKS FOR :: SEARCH, LOGIN, SIGN UP ) //
const ToolBar = styled.div`
	font-size: 16px;
	display: flex;
	flex-direction: row;
	margin: 10px 0 30px 0;
`
// LINK ELEMENTS WITHIN MENU; STYLES //
const ElementsWrapper = () => css`
	font-size: 18px;
	line-height: 40px;
	margin: 0;
	padding-left: 0;
`
const CoreWrapper = styled.ul`
	${ElementsWrapper}
	height: auto;
	margin-bottom: 10px;

	${({ theme }) => theme.MQ.XS} {
		margin-bottom: 30px;
	}
`
const BottomMenuContainer = styled.div`
	background: ${({ theme }) => theme.palette.grey[50]};
	position: relative;
	height: 100%;
	left: -3rem;
	overflow: hidden;
	padding-left: 3rem;
	width: calc(100% + 3rem);

	${({ theme }) => theme.breakpoints.up('md')} {
		height: 90%;
		left: -5rem;
		padding-left: 5rem;
		width: calc(100% + 5rem);
	}
`
const BusinessTopicsWrapper = styled.ul`
	${ElementsWrapper}
	line-height: 40px;
`

const ChevronWrapper = styled.div`
	margin-left: 0.5rem;
	transform: ${({ isExpanded }) => `rotate(${isExpanded ? '180' : '90'}deg)`};
	transition: 0.4s ease all;
`
const Element = styled.li`
	list-style-type: none;
	display: flex;
	margin-top: 1rem;
	width: 100%;

	&:first-child {
		margin-top: 0;
	}

	svg {
		margin: 10px 0.75rem auto 0;
		width: 24px;
	}
`
const FeaturedSlider = withStyles(
	{
		root: {
			position: 'absolute',
			top: '0',
			zIndex: 3,
		},
	},
	{ name: 'featuredSlider' }
)(Slide)

const FeaturedPanel = withStyles(
	{
		root: {
			borderRadius: '0',
			height: '100%',
			left: '0',
			overflowY: 'auto',
		},
	},
	{ name: 'featuredPanel' }
)(Paper)
const FeaturedSliderTablet = withStyles(
	{
		root: {
			background: '#003B5C',
			boxShadow: 'none',
			height: '100%',
			marginLeft: '350px',
			position: 'fixed',
			top: '0',
			maxWidth: '300px',
			zIndex: '-1',
		},
	},
	{ name: 'featuredSliderTablet' }
)(Slide)
const FeaturedPanelTablet = withStyles(
	{
		root: {
			height: '100%',
			width: '100%',
		},
	},
	{ name: 'featuredPanelTablet' }
)(Paper)
const Accordion = withStyles(
	{
		root: {
			'&.MuiPaper-root': {
				background: '#F2F5F7',
				boxShadow: 'none',
				border: 'none',
			},
			'&:before': {
				background: 'none',
			},
		},
	},
	{ name: 'accordion' }
)(MuiAccordion)
const AccordionSummary = withStyles(
	{
		root: {
			padding: 0,
			'&.Mui-expanded': {
				minHeight: 'auto',
			},
		},
		content: {
			margin: 0,
			'&.Mui-expanded': {
				margin: 0,
			},
		},
	},
	{ name: 'accordionSummary' }
)(MuiAccordionSummary)
const AccordionDetails = withStyles(
	{
		root: {
			display: 'flex',
			flexDirection: 'column',
			marginLeft: '1rem',
			padding: '0 0 8px 0',
		},
	},
	{ name: 'accordionDetails' }
)(MuiAccordionDetails)

const SVG_COMPONENTS = {
	community: CommunityIcon,
	advertise: MegaPhone,
}

const handleHubLink = (e, slug) => {
	e.preventDefault()
	trackNavEvent({ destination: slug, type: 'Hamburger Hub link' })
}
const handleButtonClick = (slug) => {
	trackNavEvent({ destination: slug, type: 'Hamburger See All button' })
	window.location.href = `/${slug}`
}
/**
 * This Component is the Hamburger Menu
 * It should show the Hamburger and site logo
 *
 * On Open: the menu should slide out from the left side + the hamburger turns into an X
 * A primary overlay should take over the screen, nothing on the screen should be clickable
 * Desktop: If the user is logged in a second panel also slides out (a little after the regular menu)
 * Search Panel will slide out when the search link is clicked
 *
 * On Close (X): the menu + any panels should slide back into the left side and the primary overlay should disapear
 * Back to Menu: Any back to menu links only show if a panel is opened from the Menu
 * clicking back to menu will reveal the menu again
 *
 * @param {Function} props.handleMenuStatus :: handles hamburger menu open and close
 * @param {Function} props.handleSearchClick :: handles search panel open and close
 * @param {Function} props.handleUserRegModal :: handles sign in and log in pop up modals
 * @param {Boolean} props.isHamburgerMenuOpen :: determines if hamburger menu is open
 */
export function SubHubHamburgerMenu({ handleMenuStatus, handleSearchClick, isHamburgerMenuOpen }) {
	const {
		siteConstants: {
			EMAIL_CAPTURE_SOURCES: { subscribeButton },
			HAMBURGER_MENU_CORE_LINKS,
			HUB_LINKS,
		},
	} = useContext(SiteConfigContext)

	const { emailSubscribeFormSubmitted, setEmailSubscribeSource, setIsVisible } = useContext(EmailSubscribeContext)

	const {
		search: { strokeColor },
		colors,
	} = useTheme()

	const [expandedHub, setExpandedHub] = useState(false)
	const [isTrendingTopicOpen, setIsTrendingTopicOpen] = useState(false)
	const [isTabletHubOpen, setIsTabletHubOpen] = useState(false)

	useEffect(() => {
		setIsTrendingTopicOpen(false)
		setExpandedHub(false)
	}, [isHamburgerMenuOpen])

	const handleAccordionChange = (panel) => (event, newExtended) => {
		setExpandedHub(newExtended ? panel : false)
		setIsTabletHubOpen(newExtended)
	}

	const handleFeaturedTopics = (event) => {
		event.preventDefault()
		setIsTrendingTopicOpen(!isTrendingTopicOpen)
	}

	const classes = useStyles({ isHamburgerMenuOpen, hamburgerMenuWidth: HAMBURGER_MENU_WIDTH })()

	return (
		<div className={classes.SubHubHamburgerMenuWrapper}>
			{/* Menu Overlay */}
			<div className={classes.MenuOverlay}>
				<MenuContainer>
					<div className={classes.Menu}>
						{/* TOOL BAR */}
						{/* Desktop :: default shows the search, login and sign up links */}
						<Hidden only="xs">
							<ToolBar>
								<Link
									className={clsx(classes.StyledLink, 'search', 'svgOverride')}
									onClick={(e) => handleSearchClick(e)}
									variant="primary"
								>
									Search
									<SearchIcon height={17} width={17} strokeColor={strokeColor} />
								</Link>
								{!emailSubscribeFormSubmitted && (
									<>
										<Separator>|</Separator>
										<Link
											className={classes.StyledLink}
											onClick={(e) => {
												handleMenuStatus(e, { menuType: 'hamburger' })
												setIsVisible(true)
												setEmailSubscribeSource(subscribeButton)
											}}
											variant="primary"
										>
											Subscribe
										</Link>
									</>
								)}
							</ToolBar>
						</Hidden>

						{/* MENU ELEMENTS */}
						{/* Core Value Props */}
						<CoreWrapper>
							{HAMBURGER_MENU_CORE_LINKS.map((item, i) => {
								const GetIcon = SVG_COMPONENTS[item.icon]
								return (
									<Element key={i}>
										<Link
											className={classes.StyledLink}
											href={item.slug}
											{...(item.trackingName && {
												onClick: () =>
													trackNavEvent({ destination: item.slug, type: item.trackingName }),
											})}
											target={item.target}
											variant="primary"
										>
											<GetIcon />
											{item.title}
										</Link>
									</Element>
								)
							})}
						</CoreWrapper>
						{/* Business Basics */}
						<BottomMenuContainer>
							<div className={classes.BusinessTopicsTitle}>Business Topics</div>
							<HubsContainer>
								{HUB_LINKS.map((item, i) => {
									const panel = `panel-${i}`
									const isExpanded = expandedHub === panel
									const isOpenLarge = isTabletHubOpen && isExpanded
									const isOpenSmall = isTrendingTopicOpen && isExpanded
									return (
										<React.Fragment key={`hub-${i}`}>
											<Hidden smDown initialWidth="xs">
												<FeaturedSliderTablet
													direction="right"
													in={isOpenLarge}
													aria-expanded={isOpenLarge}
												>
													<FeaturedPanelTablet>
														<FeaturedTopics
															handlePanel={handleFeaturedTopics}
															showBackLink={false}
															topics={HUB_LINKS[i].featuredTopics}
														/>
													</FeaturedPanelTablet>
												</FeaturedSliderTablet>
											</Hidden>
											<Hidden mdUp initialWidth="xs">
												<FeaturedSlider
													direction="right"
													in={isOpenSmall}
													aria-expanded={isOpenSmall}
												>
													<FeaturedPanel>
														<FeaturedTopics
															handlePanel={handleFeaturedTopics}
															showBackLink={true}
															topics={HUB_LINKS[i].featuredTopics}
														/>
													</FeaturedPanel>
												</FeaturedSlider>
											</Hidden>
											<BusinessTopicsWrapper>
												<Accordion
													expanded={isExpanded}
													onChange={handleAccordionChange(panel, item.featuredTopics)}
												>
													<AccordionSummary>
														<HubTitleWrapper key={i}>
															<Link
																aria-expanded={isExpanded}
																className={classes.HubTitle}
																href={`/${item.slug}`}
																onClick={(e) => handleHubLink(e, item.slug)}
																variant="primary"
															>
																{item.title}
															</Link>
															{isExpanded && (
																<Button
																	aria-label={`See all ${item.title}`}
																	className={classes.SeeAllButton}
																	onClick={() => handleButtonClick(item.slug)}
																>
																	See All
																</Button>
															)}
															<ChevronWrapper isExpanded={isExpanded}>
																<Chevron
																	color={colors.primary}
																	height={12}
																	width={12}
																/>
															</ChevronWrapper>
														</HubTitleWrapper>
													</AccordionSummary>
													<AccordionDetails>
														<Hidden mdUp initialWidth="xs">
															<Link
																className={clsx(
																	classes.StyledLink,
																	classes.PanelLink,
																	'svgOverride',
																	'featuredTopic'
																)}
																href="#"
																onClick={(e) => handleFeaturedTopics(e)}
																variant="primary"
															>
																Featured Topics
																<FeaturedArrow>
																	<Arrow height={15} width={15} variant="primary" />
																</FeaturedArrow>
															</Link>
														</Hidden>

														{item.subHubs &&
															item.subHubs.map((subHub, i) => (
																<Element key={`sub-hub-link-${i}`}>
																	<Link
																		className={clsx(
																			classes.StyledLink,
																			classes.PanelLink
																		)}
																		href={`/${subHub.slug}/`}
																		onClick={() =>
																			trackNavEvent({
																				destination: subHub.slug,
																				type: 'Hamburger SubHub link',
																			})
																		}
																		variant="primary"
																	>
																		{subHub.title}
																	</Link>
																</Element>
															))}
													</AccordionDetails>
												</Accordion>
											</BusinessTopicsWrapper>
										</React.Fragment>
									)
								})}
							</HubsContainer>
						</BottomMenuContainer>
					</div>
				</MenuContainer>
			</div>
		</div>
	)
}
SubHubHamburgerMenu.propTypes = {
	isDisplaySoloSearchPanel: bool,
	updateSoloSearchVisibility: func,
}
